// ST-1258 -Claude- Landing page: search, shippable deals, nearby stores, brand rail, hot products.
// ST-1281 -Claude- Added LocationInput typeahead so users can find businesses by city/PIN code.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchBar, ProductTile, Card } from '@nearrme/ui-web';
import { useQuery } from '@tanstack/react-query';
import { searchProducts, searchSuggestions } from '@nearrme/api-client';
import { getApiClient } from '../api/client';
import type { ConsumerProductSummary } from '@nearrme/types';
import { LocationInput } from '../components/LocationInput';
import { useLocation } from '../hooks/useLocation';
import { BAG_MARK_SVG } from '@nearrme/brand';

const ONLINE_ONLY_DEALS = [
  {
    title: 'TechPro X2 Pro verified deal',
    brand: 'TechPro',
    offer: 'Online only on Nearrme',
    price: 'From Rs. 24,999',
    note: 'Original manufacturer warranty with verified invoice.',
    action: 'Order for delivery',
    tone: 'from-emerald-600 to-slate-900',
  },
  {
    title: 'HomeEase Air Purifier bundle',
    brand: 'HomeEase',
    offer: 'Online only on Nearrme',
    price: 'From Rs. 8,499',
    note: 'Manufacturer-backed service path and local support.',
    action: 'Buy shipped deal',
    tone: 'from-sky-600 to-emerald-700',
  },
  {
    title: 'GadgetWorld Phone Lite',
    brand: 'GadgetWorld',
    offer: 'Online only on Nearrme',
    price: 'From Rs. 12,999',
    note: 'Nearrme can route the order to a verified fulfillment partner.',
    action: 'Post order',
    tone: 'from-violet-600 to-slate-900',
  },
];

const STORE_DEALS = [
  { name: 'Phone accessories in stock', store: 'Western Traders', city: 'Mumbai', distance: '1.8 km', offer: 'Available at store' },
  { name: 'Appliance service-backed deal', store: 'Powai Mart', city: 'Mumbai', distance: '2.4 km', offer: 'Available at store' },
  { name: 'Laptop bundle savings', store: 'Tech Zone Bangalore', city: 'Bangalore', distance: '3.1 km', offer: 'Available at store' },
  { name: 'Verified invoice pickup', store: 'Gujarat Electronics', city: 'Ahmedabad', distance: '4.2 km', offer: 'Available at store' },
];

const BRAND_RAIL = [
  { name: 'TechPro', mark: 'TP', tone: 'from-emerald-600 to-slate-900' },
  { name: 'HomeEase', mark: 'HE', tone: 'from-sky-600 to-emerald-700' },
  { name: 'GadgetWorld', mark: 'GW', tone: 'from-violet-600 to-slate-900' },
  { name: 'FreshMart', mark: 'FM', tone: 'from-lime-600 to-emerald-800' },
  { name: 'UrbanStyle', mark: 'US', tone: 'from-rose-600 to-slate-900' },
  { name: 'NutriLife', mark: 'NL', tone: 'from-amber-500 to-emerald-800' },
];

const HOT_PRODUCTS = [
  { name: 'TechPro Notebook 15', brand: 'TechPro', price: 'From Rs. 39,999', action: 'Check stock or delivery' },
  { name: 'HomeEase Water Purifier', brand: 'HomeEase', price: 'From Rs. 10,499', action: 'Find verified seller' },
  { name: 'GadgetWorld Tablet Mini', brand: 'GadgetWorld', price: 'From Rs. 15,999', action: 'Order through Nearrme' },
];

export function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { location } = useLocation();
  const trimmedQuery = query.trim();

  const { data: suggestionResponse, isFetching: suggestionsLoading } = useQuery({
    queryKey: ['search-suggestions', trimmedQuery],
    queryFn: () => searchSuggestions(getApiClient(), trimmedQuery, 8),
    enabled: trimmedQuery.length >= 2,
    staleTime: 60 * 1000,
  });

  const { data: hotDeals } = useQuery({
    queryKey: ['hot-deals', location?.lat, location?.lng],
    queryFn: () =>
      searchProducts(getApiClient(), {
        q: 'phone',
        pageSize: 10,
        lat: location?.lat,
        lng: location?.lng,
      }),
    staleTime: 5 * 60 * 1000,
  });

  const handleSearch = (q: string) => {
    if (!q.trim()) return;
    const params = new URLSearchParams({ q: q.trim() });
    if (location?.lat) params.set('lat', String(location.lat));
    if (location?.lng) params.set('lng', String(location.lng));
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="animate-fade-in">
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950">
        <div className="max-w-5xl mx-auto px-4 pt-10 pb-16 sm:pt-14 sm:pb-20 text-center">
          <div
            className="mx-auto mb-4 h-20 w-20 sm:h-24 sm:w-24"
            dangerouslySetInnerHTML={{ __html: BAG_MARK_SVG }}
          />
          <div className="mb-4 text-5xl sm:text-6xl font-light tracking-[0.1em] text-slate-950 dark:text-slate-50">
            Nea<span className="font-normal text-accent-red">r</span><span className="font-normal text-brand-500 dark:text-brand-400">r</span>me
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-display">
            {t('landing.heroTitle')}
          </h1>
          <div className="mt-8 max-w-2xl mx-auto">
            <SearchBar
              value={query}
              onChange={setQuery}
              onSubmit={handleSearch}
              placeholder={t('landing.searchPlaceholder')}
              size="lg"
              suggestions={suggestionResponse?.data ?? []}
              suggestionsLoading={suggestionsLoading}
              onSuggestionSelect={(suggestion) => handleSearch(suggestion.query)}
            />
          </div>

          <div className="mt-6">
            <LocationInput />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
              Delivery and local pickup
            </p>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Hot deals
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Online-only deals are shipped through Nearrme. Store deals are available at verified nearby stores.
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleSearch('hot deals')}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 dark:border-slate-700 px-4 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            View deals
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-bold text-slate-950 dark:text-slate-50 mb-3">
              Online-only on Nearrme
            </h3>
            <div className="grid sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3 gap-4">
              {ONLINE_ONLY_DEALS.map((deal) => (
                <button
                  key={deal.title}
                  type="button"
                  onClick={() => handleSearch(deal.title)}
                  className="text-left rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
                >
                  <div className={`h-28 bg-gradient-to-br ${deal.tone} p-4 text-white flex flex-col justify-between`}>
                    <div className="text-xs font-semibold uppercase tracking-wide opacity-90">{deal.offer}</div>
                    <div className="text-xl font-black leading-tight">{deal.price}</div>
                  </div>
                  <div className="p-4">
                    <div className="text-sm font-bold text-slate-950 dark:text-slate-50">{deal.title}</div>
                    <div className="mt-1 text-xs font-semibold text-brand-700 dark:text-brand-300">{deal.brand}</div>
                    <p className="mt-2 text-sm leading-5 text-slate-600 dark:text-slate-300">{deal.note}</p>
                    <div className="mt-4 text-sm font-semibold text-slate-900 dark:text-slate-100">{deal.action}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <Card padded>
            <h3 className="text-lg font-bold text-slate-950 dark:text-slate-50">
              Available at store
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Visit a verified nearby store for pickup, inspection, invoice, and local service.
            </p>
            <div className="mt-4 grid gap-3">
              {STORE_DEALS.map((deal) => (
                <button
                  key={`${deal.store}-${deal.name}`}
                  type="button"
                  onClick={() => handleSearch(deal.name)}
                  className="w-full text-left rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-3 hover:bg-slate-50 dark:hover:bg-slate-900"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">{deal.offer}</div>
                      <div className="mt-1 text-sm font-bold text-slate-950 dark:text-slate-50">{deal.name}</div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{deal.store} - {deal.city}</div>
                    </div>
                    <div className="text-xs font-semibold text-brand-700 dark:text-brand-300">{deal.distance}</div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-12">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
          Hot products
        </h2>
        {hotDeals?.data?.items.length ? (
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 scroll-smooth snap-x">
            {hotDeals.data.items.slice(0, 6).map((p: ConsumerProductSummary) => (
              <div key={p.productId} className="snap-start">
                <ProductTile
                  product={p}
                  variant="rail"
                  onClick={(prod) => navigate(`/p/${prod.productId}`)}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {HOT_PRODUCTS.map((product) => (
              <button
                key={product.name}
                type="button"
                onClick={() => handleSearch(product.name)}
                className="text-left rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 hover:border-brand-300 dark:hover:border-brand-700"
              >
                <div className="h-24 rounded-lg bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                  <div className="h-14 w-14 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 flex items-center justify-center text-sm font-black text-brand-700 dark:text-brand-300">
                    {product.brand.slice(0, 2).toUpperCase()}
                  </div>
                </div>
                <div className="mt-4 text-sm font-bold text-slate-950 dark:text-slate-50">{product.name}</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{product.brand}</div>
                <div className="mt-3 text-sm font-semibold text-slate-800 dark:text-slate-100">{product.price}</div>
                <div className="mt-3 text-sm font-semibold text-brand-700 dark:text-brand-300">{product.action}</div>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="border-y border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
                Authorized dealers near you
              </p>
              <h2 className="text-2xl font-bold text-slate-950 dark:text-slate-50">
                Manufacturer verified stores
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
                Find local stores that can support genuine products, original manufacturer warranty, verified invoice, and after-sales help.
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleSearch('phone')}
              className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
            >
              Find dealers
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {BRAND_RAIL.map((brand) => (
              <button
                key={brand.name}
                type="button"
                onClick={() => handleSearch(brand.name)}
                className="min-h-24 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3 flex flex-col items-center justify-center text-center hover:border-brand-300 dark:hover:border-brand-700"
              >
                <div className={`h-11 w-11 rounded-lg bg-gradient-to-br ${brand.tone} text-white flex items-center justify-center text-sm font-black tracking-wide shadow-sm`}>
                  {brand.mark}
                </div>
                <div className="mt-2 text-sm font-bold text-slate-800 dark:text-slate-100">{brand.name}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-center text-slate-900 dark:text-slate-100 mb-10">
          {t('landing.howItWorks')}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { num: '1', title: t('landing.step1'), icon: 'Search' },
            { num: '2', title: t('landing.step2'), icon: 'Store' },
            { num: '3', title: t('landing.step3'), icon: 'Buy' },
          ].map((step) => (
            <Card key={step.num} className="text-center">
              <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 flex items-center justify-center text-sm font-black">
                {step.icon}
              </div>
              <div className="text-sm font-bold text-brand-600 dark:text-brand-300">STEP {step.num}</div>
              <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                {step.title}
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
